import { BEARER_GROUPS } from "../../pages/OfficeBearers";

export const ALBUM_CATEGORIES = ["Tournaments", "Events", "Training", "Association Activities"];
const PHOTO_HINT = "JPG, PNG or WEBP. Large images are automatically resized and optimised.";
const off = (k, on, offLabel) => (it) => (it[k] === false ? [offLabel] : on ? [on] : []);

export const RESOURCES = {
  news: {
    label: "News & Announcements",
    titleKey: "title",
    subKey: "date",
    search: ["title", "description"],
    filters: [{ name: "status", label: "Status", options: ["Published", "Draft"] }, { name: "category", label: "Category", options: ["Announcement", "Tournament", "Selection", "Result", "Coaching", "Association Event"] }],
    badges: (it) => (it.status === "Draft" ? ["Draft"] : []),
    fields: [
      { name: "title", label: "Title", required: true },
      { name: "status", label: "Status", type: "select", options: ["Published", "Draft"] },
      { name: "date", label: "Publication date", type: "date" },
      { name: "category", label: "Category", type: "select", options: ["Announcement", "Tournament", "Selection", "Result", "Coaching", "Association Event"] },
      { name: "description", label: "Description", type: "textarea", rows: 3 },
      { name: "content", label: "Full article", type: "textarea", rows: 8 },
      { name: "image", label: "Featured image", type: "image", hint: PHOTO_HINT },
      { name: "external_link", label: "External link (optional)" },
    ],
  },
  events: {
    label: "Events & Training",
    titleKey: "title",
    subKey: "status",
    fields: [
      { name: "title", label: "Event name", required: true },
      { name: "type", label: "Type", type: "select", options: ["Tournament", "Coaching Programme", "Training", "Coaching Camp", "Association Event"] },
      { name: "status", label: "Status", type: "select", options: ["Upcoming", "Ongoing", "Completed"] },
      { name: "date", label: "Start date", type: "date" },
      { name: "end_date", label: "End date (optional)", type: "date" },
      { name: "start_time", label: "Start time", type: "time" },
      { name: "end_time", label: "End time", type: "time" },
      { name: "venue", label: "Venue" },
      { name: "organizer", label: "Trainer / Organizer" },
      { name: "level", label: "Age category / Skill level" },
      { name: "description", label: "Description", type: "textarea", rows: 5 },
      { name: "image", label: "Cover image", type: "image" },
      { name: "photos", label: "Event photographs", type: "images" },
    ],
  },
  players: {
    label: "Player Management",
    titleKey: "name",
    subKey: "playing_category",
    search: ["name", "club", "tnba_id", "district"],
    filters: [
      { name: "gender", label: "Gender", options: ["Male", "Female", "Other"] },
      { name: "play_type", label: "Format", options: ["Singles", "Doubles", "Singles & Doubles"] },
      { name: "active", label: "Status", options: [true, false], labels: ["Active", "Inactive"] },
    ],
    badges: off("active", null, "Inactive"),
    note: "All players are listed on the public Players page with their Active / Inactive status.",
    fields: [
      { name: "name", label: "Player name", required: true },
      { name: "photo", label: "Profile photo", type: "image", round: true, hint: PHOTO_HINT },
      { name: "dob", label: "Date of birth (admin only – never shown publicly)", type: "date" },
      { name: "gender", label: "Gender", type: "select", options: ["", "Male", "Female", "Other"] },
      { name: "playing_category", label: "Playing category (e.g. U-15 Boys, Men's Open)" },
      { name: "play_type", label: "Singles / Doubles", type: "select", options: ["", "Singles", "Doubles", "Singles & Doubles"] },
      { name: "club", label: "Club / Academy" },
      { name: "district", label: "Playing district (optional)" },
      { name: "achievements", label: "Achievements", type: "textarea", rows: 4 },
      { name: "tnba_id", label: "Player ID / TNBA ID (optional, shown publicly)" },
      { name: "active", label: "Status", type: "switch", on: "Active", off: "Inactive" },
    ],
  },
  achievements: {
    label: "Achievements",
    titleKey: "title",
    subKey: "year",
    fields: [
      { name: "title", label: "Title", required: true },
      { name: "year", label: "Year" },
      { name: "category", label: "Category", type: "select", options: ["Association Milestone", "Player Achievement", "Tournament", "Coaching Achievement"] },
      { name: "description", label: "Description", type: "textarea", rows: 4 },
      { name: "venue", label: "Venue (optional)" },
      { name: "tournament_details", label: "Tournament details (optional)", type: "textarea", rows: 3 },
      { name: "photos", label: "Photographs", type: "images" },
    ],
  },
  bearers: {
    label: "Office Bearers",
    titleKey: "name",
    subKey: "designation",
    search: ["name", "designation"],
    filters: [{ name: "active", label: "Status", options: [true, false], labels: ["Active", "Archived"] }],
    reorder: true,
    badges: off("active", null, "Archived"),
    fields: [
      { name: "name", label: "Full name", required: true },
      { name: "designation", label: "Designation", type: "combo", required: true, options: ["President", "Senior Vice President", "Vice President", "Secretary", "Treasurer", "Joint Secretary", "Executive Committee Member"] },
      { name: "bio", label: "Short biography", type: "textarea", rows: 3 },
      { name: "contact", label: "Contact information – phone or email (optional, shown publicly)" },
      { name: "group", label: "Section", type: "select", options: BEARER_GROUPS },
      { name: "active", label: "Status", type: "switch", on: "Active – shown publicly", off: "Archived – hidden" },
    ],
  },
  sponsors: {
    label: "Sponsors",
    titleKey: "name",
    subKey: "tier",
    reorder: true,
    badges: off("active", null, "Hidden"),
    fields: [
      { name: "name", label: "Sponsor name", required: true },
      { name: "logo", label: "Logo", type: "image", hint: "PNG with transparent background works best." },
      { name: "website", label: "Website URL" },
      { name: "tier", label: "Tier / type (e.g. Title Sponsor, Partner)" },
      { name: "description", label: "Short description", type: "textarea", rows: 2 },
      { name: "active", label: "Status", type: "switch", on: "Shown publicly", off: "Hidden" },
    ],
  },
  leaders: {
    label: "Our Leadership (About page)",
    titleKey: "name",
    subKey: "designation",
    reorder: true,
    note: "These profiles appear on the About page under \"Our Leadership\". A neutral placeholder is shown until a photo is uploaded.",
    fields: [
      { name: "name", label: "Full name", required: true },
      { name: "designation", label: "Designation", type: "select", options: ["Founder", "President", "Secretary", "Treasurer"] },
      { name: "photo", label: "Profile photo", type: "image", round: true, accept: "image/jpeg,image/png,image/webp", hint: "JPG, PNG or WEBP. A portrait, face-centred photo looks best." },
    ],
  },
  about_sections: {
    label: "Additional About Sections",
    titleKey: "title",
    subKey: "content",
    reorder: true,
    badges: off("published", null, "Unpublished"),
    fields: [
      { name: "title", label: "Section title", required: true },
      { name: "content", label: "Content", type: "textarea", rows: 6 },
      { name: "image", label: "Image (optional)", type: "image", hint: PHOTO_HINT },
      { name: "published", label: "Status", type: "switch", on: "Published", off: "Unpublished" },
    ],
  },
  documents: {
    label: "Documents & PDFs",
    titleKey: "title",
    subKey: "file_name",
    fields: [
      { name: "title", label: "Title", required: true },
      { name: "description", label: "Description" },
      { name: "file", label: "File (PDF / DOC)", type: "file", fileNameField: "file_name", required: true },
    ],
  },
};
